/**
 * 인앱 컨텍스트 메뉴. 브라우저 기본 메뉴 대신 우클릭에 우리 동작을 붙인다.
 * showContextMenu(x, y, items, opts): items = [{label, onClick, danger, disabled, hint},
 * {label, children:[items]} (하위 메뉴 ▸: hover·클릭·→ 로 열리고 ← 로 닫힌다),
 * {separator: true}, {colors: ['c1',…], onColor(c)}]. 바깥 클릭·Esc·스크롤에 닫힌다.
 * it.icon: 항목 앞 아이콘 — 모듈 상수의 SVG 마크업만(innerHTML 로 넣는다, 사용자 글자는 절대 아님).
 * 한 항목이라도 아이콘이 있으면 같은 메뉴의 나머지 항목은 빈 칸으로 줄을 맞춘다. 아이콘은 라벨 뒤의 <i> 이고
 * CSS order 로 앞에 보인다 — 라벨 <span> 이 여전히 첫 자식이라 `.ctx-item > span:first-child` 를 잡는 코드는 그대로다.
 * opts.placement = 'top' 이면 (x, y) 를 메뉴의 왼쪽 아래 모서리로 잡는다 (하단 선택바 위로 펼칠 때).
 * 키보드: ↑↓ Home End 이동, → 하위 메뉴, ← 상위로, Enter/Space 선택, Esc 전체 닫기.
 */
import { openExclusive, closedExclusive } from './popover-registry.js';

let open = null; // 최상위 메뉴
let subs = []; // 열린 하위 메뉴 (깊이 순)
let restoreFocus = null;

function allMenus() { return open ? [open].concat(subs) : []; }

export function closeContextMenu() {
    closedExclusive('context-menu');
    if (!open) return;
    const menus = allMenus();
    const hadFocus = menus.some((m) => m.contains(document.activeElement));
    menus.forEach((m) => m.remove());
    open = null;
    subs = [];
    if (hadFocus && restoreFocus && restoreFocus.isConnected && restoreFocus.focus) restoreFocus.focus({ preventScroll: true });
    restoreFocus = null;
}

// depth 보다 깊은 하위 메뉴를 닫는다 (depth 0 = 최상위만 남김)
function closeSubs(depth) {
    while (subs.length > depth) {
        const m = subs.pop();
        if (m._parentItem) m._parentItem.classList.remove('open');
        m.remove();
    }
}

function itemsOf(menu) {
    return Array.from(menu.querySelectorAll(':scope > .ctx-item:not(:disabled)'));
}

function focusItem(menu, index) {
    const its = itemsOf(menu);
    if (!its.length) return;
    const i = ((index % its.length) + its.length) % its.length;
    its[i].focus({ preventScroll: true });
}

// 화면 밖으로 나가지 않게: (x, y) 는 왼쪽 위 모서리, placement 'top' 이면 왼쪽 아래 모서리.
// 크기는 offsetWidth/Height — 등장 효과(pop-in)의 scale 이 도는 동안 getBoundingClientRect 는 2% 작다.
function place(menu, x, y, placement) {
    const r = { width: menu.offsetWidth, height: menu.offsetHeight };
    let top = placement === 'top' ? y - r.height : y;
    const left = Math.min(x, window.innerWidth - r.width - 8);
    top = Math.min(top, window.innerHeight - r.height - 8);
    menu.style.left = Math.max(4, left) + 'px';
    menu.style.top = Math.max(4, top) + 'px';
}

// 하위 메뉴는 부모 항목의 오른쪽에, 자리가 없으면 왼쪽에
function placeSub(menu, item) {
    const ir = item.getBoundingClientRect();
    const r = menu.getBoundingClientRect();
    let left = ir.right + 2;
    if (left + r.width > window.innerWidth - 8) left = Math.max(4, ir.left - r.width - 2);
    let top = ir.top - 4;
    top = Math.min(top, window.innerHeight - r.height - 8);
    menu.style.left = left + 'px';
    menu.style.top = Math.max(4, top) + 'px';
}

function openSub(item, depth, focusFirst) {
    if (subs[depth] && subs[depth]._parentItem === item) { // 이미 열림
        if (focusFirst) focusItem(subs[depth], 0);
        return subs[depth];
    }
    closeSubs(depth);
    const menu = build(item._children, depth + 1);
    menu.classList.add('ctx-sub');
    menu._parentItem = item;
    item.classList.add('open');
    item.setAttribute('aria-expanded', 'true');
    document.body.appendChild(menu);
    placeSub(menu, item);
    subs.push(menu);
    if (focusFirst) focusItem(menu, 0);
    return menu;
}

function build(items, depth) {
    const menu = document.createElement('div');
    menu.className = 'ctx-menu';
    menu.setAttribute('role', 'menu');
    menu._depth = depth;
    const withIcons = items.some((it) => it && it.icon);
    if (withIcons) menu.classList.add('ctx-has-icons');
    items.forEach((it) => {
        if (it.separator) {
            const sep = document.createElement('div');
            sep.className = 'ctx-sep';
            menu.appendChild(sep);
            return;
        }
        if (it.colors) {
            const row = document.createElement('div');
            row.className = 'ctx-colors';
            it.colors.forEach((c) => {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'cal-' + c;
                b.addEventListener('click', () => { closeContextMenu(); it.onColor(c); });
                row.appendChild(b);
            });
            menu.appendChild(row);
            return;
        }
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ctx-item' + (it.danger ? ' danger' : '');
        b.disabled = !!it.disabled;
        b.setAttribute('role', 'menuitem');
        const label = document.createElement('span');
        label.className = 'ctx-label';
        label.textContent = it.label;
        b.appendChild(label);
        if (withIcons) {
            const ic = document.createElement('i');
            ic.className = 'ctx-icon';
            ic.setAttribute('aria-hidden', 'true');
            if (it.icon) ic.innerHTML = it.icon;
            b.appendChild(ic);
        }
        if (it.hint) {
            const h = document.createElement('kbd');
            h.textContent = it.hint;
            b.appendChild(h);
        }
        const children = Array.isArray(it.children) ? it.children : null;
        if (children) {
            b.classList.add('has-sub');
            b.setAttribute('aria-haspopup', 'menu');
            b.setAttribute('aria-expanded', 'false');
            const arrow = document.createElement('i');
            arrow.className = 'ctx-arrow';
            arrow.setAttribute('aria-hidden', 'true');
            arrow.textContent = '▸';
            b.appendChild(arrow);
            b._children = children;
            b.addEventListener('mouseenter', () => { if (!b.disabled) openSub(b, depth, false); });
            b.addEventListener('click', () => { if (b.disabled) return; if (subs[depth] && subs[depth]._parentItem === b) closeSubs(depth); else openSub(b, depth, true); });
        } else {
            b.addEventListener('mouseenter', () => closeSubs(depth));
            b.addEventListener('click', () => { closeContextMenu(); it.onClick(); });
        }
        menu.appendChild(b);
    });
    // 메뉴 위로 마우스가 돌아오면 더 깊은 하위 메뉴만 닫는다 (구분선·여백 위에서)
    menu.addEventListener('mouseenter', () => closeSubs(depth));
    return menu;
}

export function showContextMenu(x, y, items, opts) {
    closeContextMenu();
    const o = opts || {};
    restoreFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
    const menu = build(items, 0);
    if (o.placement === 'top') menu.dataset.placement = 'top'; // 등장 효과가 아래 모서리에서
    document.body.appendChild(menu);
    place(menu, x, y, o.placement);
    open = menu;
    // 배타성만 등록부에 맡긴다: 하위 메뉴까지 아우르는 바깥 판정과 키보드 조작은
    // 아래의 자체 mousedown/keydown 캡처 리스너가 계속 담당한다 (manual).
    openExclusive('context-menu', closeContextMenu, { manual: true });
    return menu;
}

document.addEventListener('mousedown', (e) => { if (open && !allMenus().some((m) => m.contains(e.target))) closeContextMenu(); }, true);
document.addEventListener('keydown', (e) => {
    if (!open) return;
    if (e.key === 'Escape') { closeContextMenu(); e.stopImmediatePropagation(); return; }
    const menus = allMenus();
    const active = document.activeElement;
    const cur = menus.find((m) => m.contains(active)) || null;
    const menu = cur || menus[menus.length - 1];
    const its = itemsOf(menu);
    const idx = its.indexOf(active);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault(); e.stopImmediatePropagation();
        if (idx < 0) focusItem(menu, e.key === 'ArrowDown' ? 0 : -1);
        else focusItem(menu, idx + (e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault(); e.stopImmediatePropagation();
        focusItem(menu, e.key === 'Home' ? 0 : -1);
    } else if (e.key === 'ArrowRight') {
        if (active && active._children && menu.contains(active)) { e.preventDefault(); e.stopImmediatePropagation(); openSub(active, menu._depth, true); }
    } else if (e.key === 'ArrowLeft') {
        if (menu._depth > 0) { e.preventDefault(); e.stopImmediatePropagation(); const parent = menu._parentItem; closeSubs(menu._depth - 1); if (parent) parent.focus({ preventScroll: true }); }
    }
}, true);
window.addEventListener('scroll', closeContextMenu, true);
window.addEventListener('resize', closeContextMenu);
